// src/NavBar.js
import * as React from 'react';
// Remove useNavigate if not used elsewhere
import { useLocation, Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import Tooltip from '@mui/material/Tooltip';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MemoryIcon from '@mui/icons-material/Memory';
import WavesIcon from '@mui/icons-material/Waves';
import { useAppTheme } from './contexts/ThemeContext';

import { STATION_CONFIGS } from './config/stationConfigs';

// Build pages dynamically based on current route
const getPages = (pathname) => {
  // Check if we're on a station-specific route
  const stationMatch = pathname.match(/^\/piers\/([A-Z]+)/);
  const stationCode = stationMatch ? stationMatch[1] : null;
  const stationConfig = stationCode ? STATION_CONFIGS[stationCode] : null;

  const pages = [];
  if (stationConfig) {
    pages.push({ label: `${stationCode} Piers`, path: `/piers/${stationCode}` });
  }
  pages.push({ label: 'Stations', path: '/' });
  pages.push({ label: 'TP Dashboard', path: '/tp-dashboard' });
  pages.push({ label: 'How It Works', path: '/how-it-works' });
  return pages;
};

// No disabled links in this scoped app
const disabledPages = [];

function NavBar() {
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElTheme, setAnchorElTheme] = React.useState(null);
  const { currentTheme, setTheme } = useAppTheme();
  const pages = getPages(location.pathname);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenThemeMenu = (event) => {
    setAnchorElTheme(event.currentTarget);
  };

  const handleCloseThemeMenu = () => {
    setAnchorElTheme(null);
  };

  const handleThemeSelect = (theme) => {
    setTheme(theme);
    handleCloseThemeMenu();
  };

  // No handleLogout needed

  const isActiveRoute = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get the appropriate icon for current theme
  const getThemeIcon = () => {
    switch(currentTheme) {
      case 'gradient':
        return <HomeIcon />;
      case 'nightvision':
        return <VisibilityIcon />;
      case 'cyberpunk':
        return <MemoryIcon />;
      case 'submarine':
        return <WavesIcon />;
      default:
        return <HomeIcon />;
    }
  };

  // --- Helper function for Desktop Button Styles ---
  const getDesktopButtonStyles = (page, isDisabled) => {
    const active = isActiveRoute(page.path);
    const isNightVision = currentTheme === 'nightvision';
    const isCyberpunk = currentTheme === 'cyberpunk';
    const isSubmarine = currentTheme === 'submarine';

    let styles = {
      fontWeight: 600,
      color: isCyberpunk ? '#00D9FF' : (isNightVision ? '#00ff00' : (isSubmarine ? '#00FFAA' : '#fff')),
      cursor: 'pointer',
      textShadow: isCyberpunk ? '0 0 5px rgba(0, 217, 255, 0.5)' :
                 (isNightVision ? '0 0 10px rgba(0, 255, 0, 0.8)' :
                 (isSubmarine ? '0 0 8px rgba(0, 255, 170, 0.6)' : 'none')),
      textTransform: isCyberpunk || isSubmarine ? 'uppercase' : 'none',
      letterSpacing: isCyberpunk || isSubmarine ? '0.1em' : 'normal',
      '&:hover': {
        backgroundColor: isCyberpunk ? 'rgba(255,0,60,0.15)' :
                        (isNightVision ? 'rgba(0,255,0,0.1)' :
                        (isSubmarine ? 'rgba(0,255,170,0.1)' : 'rgba(255,255,255,0.1)')),
        color: isCyberpunk ? '#FFE500' :
               (isNightVision ? '#39ff14' :
               (isSubmarine ? '#00FFCC' : '#f9b612')),
        textShadow: isCyberpunk ? '0 0 10px rgba(255, 229, 0, 0.8)' :
                   (isNightVision ? '0 0 15px rgba(57, 255, 20, 1)' :
                   (isSubmarine ? '0 0 15px rgba(0, 255, 204, 0.9)' : 'none')),
      },
    };
    if (active) styles = {
      ...styles,
      color: isCyberpunk ? '#FFE500' :
             (isNightVision ? '#39ff14' :
             (isSubmarine ? '#00FFCC' : '#f9b612')),
      fontWeight: 700,
      textShadow: isCyberpunk ? '0 0 15px rgba(255, 229, 0, 0.8)' :
                 (isNightVision ? '0 0 15px rgba(57, 255, 20, 1)' :
                 (isSubmarine ? '0 0 20px rgba(0, 255, 204, 1)' : 'none')),
    };
    if (isDisabled) styles = {
      ...styles,
      color: isCyberpunk ? 'rgba(0, 217, 255, 0.3)' :
             (isNightVision ? 'rgba(0, 255, 0, 0.3)' :
             (isSubmarine ? 'rgba(0, 255, 170, 0.3)' : 'rgba(255, 255, 255, 0.5)')),
      fontWeight: 600,
      cursor: 'not-allowed',
      textShadow: 'none',
      '&:hover': {
        backgroundColor: 'transparent',
        color: isCyberpunk ? 'rgba(0, 217, 255, 0.3)' :
               (isNightVision ? 'rgba(0, 255, 0, 0.3)' :
               (isSubmarine ? 'rgba(0, 255, 170, 0.3)' : 'rgba(255, 255, 255, 0.5)')),
        textShadow: 'none',
      },
    };
    return styles;
  };

  // --- Helper function for Mobile Button Styles ---
  const getMobileButtonStyles = (page, isDisabled) => {
    const active = isActiveRoute(page.path);
    let styles = {
      fontWeight: 400, color: '#000', width: '100%', justifyContent: 'flex-start', cursor: 'pointer',
      '&:hover': { color: '#f9b612', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    };
    if (active) styles = { ...styles, color: '#f9b612', fontWeight: 700 };
    if (isDisabled) styles = {
      ...styles, color: 'grey', fontWeight: 400, cursor: 'not-allowed',
      '&:hover': { backgroundColor: 'transparent', color: 'grey' },
    };
    return styles;
  };

  // Dynamic navbar styling based on theme
  const getNavBarStyles = () => {
    const baseStyles = {
      top: 0, left: 0, transform: 'none',
      width: '100%', maxWidth: 'none',
      borderRadius: 0, borderWidth: 0, borderStyle: 'none',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      boxShadow: 1,
      zIndex: 1300,
    };

    switch(currentTheme) {
      case 'nightvision':
        return {
          ...baseStyles,
          borderBottom: '1px solid #00ff00',
          backgroundColor: 'rgba(0, 20, 0, 0.9)',
          boxShadow: '0 2px 20px rgba(0, 255, 0, 0.3), inset 0 0 20px rgba(0, 255, 0, 0.05)',
        };
      case 'cyberpunk':
        return {
          ...baseStyles,
          borderBottom: '1px solid rgba(255, 0, 60, 0.5)',
          backgroundColor: 'rgba(12, 12, 15, 0.95)',
          boxShadow: '0 2px 20px rgba(255, 0, 60, 0.3)',
        };
      case 'submarine':
        return {
          ...baseStyles,
          borderBottom: '1px solid rgba(0, 255, 170, 0.3)',
          backgroundColor: 'rgba(0, 15, 25, 0.95)',
          boxShadow: '0 2px 20px rgba(0, 255, 170, 0.2), inset 0 0 30px rgba(0, 255, 170, 0.03)',
        };
      default: // gradient
        return {
          ...baseStyles,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(30, 30, 30, 0.3)',
        };
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={getNavBarStyles()}
    >
      <Toolbar disableGutters sx={{ px: 2 }}>
        {/* MOBILE: Hamburger Icon & Menu */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}> {/* Reduced margin */}
          <IconButton
            size="large"
            onClick={handleOpenNavMenu}
            sx={{
              color: currentTheme === 'cyberpunk' ? '#FF9500' :
                     (currentTheme === 'nightvision' ? '#00ff00' :
                     (currentTheme === 'submarine' ? '#00FFAA' : '#fff')),
              filter: currentTheme === 'cyberpunk' ? 'drop-shadow(0 0 5px rgba(255, 149, 0, 0.8))' :
                      (currentTheme === 'nightvision' ? 'drop-shadow(0 0 5px rgba(0, 255, 0, 0.8))' :
                      (currentTheme === 'submarine' ? 'drop-shadow(0 0 5px rgba(0, 255, 170, 0.8))' : 'none'))
            }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{ display: { xs: 'block', md: 'none' }, '& .MuiPaper-root': { marginTop: '8px' } }}
          >
            {pages.map((page) => {
              const isDisabled = disabledPages.includes(page.label); // Check against disabled list
              return (
                <MenuItem key={page.label} onClick={handleCloseNavMenu} disabled={isDisabled} sx={{ p: 0 }}>
                  <Button component={Link} to={page.path} disabled={isDisabled} sx={getMobileButtonStyles(page, isDisabled)}>
                    {page.label}
                  </Button>
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box sx={{ ml: 1 }}>
            <img
              src="/FullLogo_Transparent_NoBuffer-2.png"
              alt="Dispatch Logo"
              style={{
                height: '40px',
                filter: currentTheme === 'nightvision'
                  ? 'brightness(0) saturate(100%) invert(68%) sepia(97%) saturate(7498%) hue-rotate(86deg) brightness(118%) contrast(119%) drop-shadow(0 0 10px rgba(0, 255, 0, 0.8))'
                  : currentTheme === 'cyberpunk'
                  ? 'brightness(0) saturate(100%) invert(13%) sepia(94%) saturate(7466%) hue-rotate(356deg) brightness(94%) contrast(113%) drop-shadow(0 0 8px rgba(255, 0, 60, 0.8))'
                  : currentTheme === 'submarine'
                  ? 'brightness(0) saturate(100%) invert(69%) sepia(45%) saturate(2000%) hue-rotate(120deg) brightness(110%) contrast(105%) drop-shadow(0 0 8px rgba(0, 255, 170, 0.8))'
                  : 'none'
              }}
            />
          </Box>
        </Box>

        {/* DESKTOP: Nav Links */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: { md: 2, lg: 3 } }}> {/* Adjusted gap */}
          {pages.map((page) => {
            const isDisabled = disabledPages.includes(page.label); // Check against disabled list
            return (
              <Button key={page.label} component={Link} to={page.path} disabled={isDisabled} sx={getDesktopButtonStyles(page, isDisabled)}>
                {page.label}
              </Button>
            );
          })}
        </Box>

        {/* RIGHT SIDE: Theme Toggle Button */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Select Theme">
            <IconButton
              onClick={handleOpenThemeMenu}
              sx={{
                color: currentTheme === 'cyberpunk' ? '#FF9500' :
                       (currentTheme === 'nightvision' ? '#00ff00' :
                       (currentTheme === 'submarine' ? '#00FFAA' : '#fff')),
                filter: currentTheme === 'cyberpunk' ? 'drop-shadow(0 0 5px rgba(255, 149, 0, 0.8))' :
                        (currentTheme === 'nightvision' ? 'drop-shadow(0 0 5px rgba(0, 255, 0, 0.8))' :
                        (currentTheme === 'submarine' ? 'drop-shadow(0 0 5px rgba(0, 255, 170, 0.8))' : 'none')),
                '&:hover': {
                  backgroundColor: currentTheme === 'cyberpunk' ? 'rgba(255,0,60,0.2)' :
                                    (currentTheme === 'nightvision' ? 'rgba(0,255,0,0.1)' :
                                    (currentTheme === 'submarine' ? 'rgba(0,255,170,0.15)' : 'rgba(255,255,255,0.1)')),
                  color: currentTheme === 'cyberpunk' ? '#FFE500' :
                         (currentTheme === 'nightvision' ? '#39ff14' :
                         (currentTheme === 'submarine' ? '#00FFCC' : '#f9b612')),
                  filter: currentTheme === 'cyberpunk' ? 'drop-shadow(0 0 10px rgba(255, 229, 0, 1))' :
                          (currentTheme === 'nightvision' ? 'drop-shadow(0 0 10px rgba(57, 255, 20, 1))' :
                          (currentTheme === 'submarine' ? 'drop-shadow(0 0 10px rgba(0, 255, 204, 1))' : 'none'))
                }
              }}
            >
              {getThemeIcon()}
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorElTheme}
            open={Boolean(anchorElTheme)}
            onClose={handleCloseThemeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiPaper-root': {
                marginTop: '8px',
                width: '220px',
                minWidth: 'unset',
                maxWidth: 'unset'
              }
            }}
          >
            <MenuItem
              onClick={() => handleThemeSelect('gradient')}
              selected={currentTheme === 'gradient'}
              sx={{
                display: 'flex',
                gap: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(249, 182, 18, 0.1)'
                }
              }}
            >
              <HomeIcon fontSize="small" />
              Default
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeSelect('nightvision')}
              selected={currentTheme === 'nightvision'}
              sx={{
                display: 'flex',
                gap: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(249, 182, 18, 0.1)'
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
              Night Vision
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeSelect('cyberpunk')}
              selected={currentTheme === 'cyberpunk'}
              sx={{
                display: 'flex',
                gap: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(249, 182, 18, 0.1)'
                }
              }}
            >
              <MemoryIcon fontSize="small" />
              Cyberpunk
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeSelect('submarine')}
              selected={currentTheme === 'submarine'}
              sx={{
                display: 'flex',
                gap: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(249, 182, 18, 0.1)'
                }
              }}
            >
              <WavesIcon fontSize="small" />
              Submarine Sonar
            </MenuItem>
          </Menu>
        </Box>

      </Toolbar>
    </AppBar>
  );
}

export default NavBar;
